import type {
  PageResult,
  ReportCategory,
  ReportSummary,
  Warrant,
} from "@hydeck/mdt-contracts";
import {
  createResource,
  createSignal,
  For,
  type JSX,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { DumpIcon } from "../components/DumpIcon";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

type DashboardData = {
  warrants: Warrant[];
  announcements: ReportSummary[];
  bolos: ReportSummary[];
  announcementCategoryId: number | null;
  boloCategoryId: number | null;
};

export function DashboardPage() {
  const [hideWarrants, setHideWarrants] = createSignal(
    localStorage.getItem("hide-warrants") === "true",
  );
  const [data] = createResource(
    () => [currentUser()?.accessToken, hideWarrants()] as const,
    async ([token, warrantsHidden]): Promise<DashboardData> => {
      const categories = await apiRequest<ReportCategory[]>(
        "/report-categories",
        { token },
      );
      const announcements = categories.find(
        ({ name }) => name === "Announcements",
      );
      const bolo = categories.find(({ name }) => name === "BOLO");

      const [warrants, announcementPage, boloPage] = await Promise.all([
        warrantsHidden
          ? Promise.resolve<Warrant[]>([])
          : apiRequest<Warrant[]>("/warrants", { token }),
        announcements
          ? apiRequest<PageResult<ReportSummary>>("/reports", {
              token,
              query: {
                page: 1,
                limit: 50,
                reportCategoryId: announcements.id,
              },
            })
          : Promise.resolve(emptyPage<ReportSummary>()),
        bolo
          ? apiRequest<PageResult<ReportSummary>>("/reports", {
              token,
              query: { page: 1, limit: 50, reportCategoryId: bolo.id },
            })
          : Promise.resolve(emptyPage<ReportSummary>()),
      ]);

      return {
        warrants: [...warrants].sort((left, right) => {
          if (left.approvedBy === null) return -1;
          if (right.approvedBy === null) return 1;
          return (
            new Date(left.warrantExpiry).getTime() -
            new Date(right.warrantExpiry).getTime()
          );
        }),
        announcements: announcementPage.items,
        bolos: boloPage.items,
        announcementCategoryId: announcements?.id ?? null,
        boloCategoryId: bolo?.id ?? null,
      };
    },
  );

  onMount(() => {
    const sync = () =>
      setHideWarrants(localStorage.getItem("hide-warrants") === "true");
    document.body.addEventListener("warrantsModeChange", sync);
    onCleanup(() =>
      document.body.removeEventListener("warrantsModeChange", sync),
    );
  });

  return (
    <div class="flex h-full w-full flex-col gap-4 lg:flex-row">
      <Show when={!hideWarrants()}>
        <section class="block w-full lg:w-1/3">
          <DumpList
            label="Warrants"
            loading={data.loading}
            entries={(data()?.warrants ?? []).map((warrant) => ({
              id: warrant.id,
              title: warrant.citizenName,
              description: warrant.description,
              icon: (
                <img
                  src="https://assets.nopixel.net/dev/images/mdt/mdt_no_profile_picture.png"
                  alt=""
                />
              ),
              leftLabel: `Incident ${warrant.incidentId}`,
              rightLabel: expiresIn(warrant.warrantExpiry),
              danger: warrant.approvedBy === null,
            }))}
          />
        </section>
      </Show>

      <Show
        when={hasPermission(
          "reports.view",
          data()?.announcementCategoryId?.toString(),
        )}
      >
        <section class="block w-full lg:w-1/3">
          <DumpList
            label="Announcements"
            loading={data.loading}
            entries={(data()?.announcements ?? []).map((report) => ({
              id: report.id,
              title: report.title,
              description: report.description,
              icon: <DumpIcon name="document_key" />,
              leftLabel: `ID: ${report.id}`,
            }))}
          />
        </section>
      </Show>

      <Show
        when={hasPermission(
          "reports.view",
          data()?.boloCategoryId?.toString(),
        )}
      >
        <section class="block w-full lg:w-1/3">
          <DumpList
            label="BOLO"
            loading={data.loading}
            entries={(data()?.bolos ?? []).map((report) => ({
              id: report.id,
              title: report.title,
              description: report.description,
              icon: <DumpIcon name="document_key" />,
              leftLabel: `ID: ${report.id}`,
            }))}
          />
        </section>
      </Show>
    </div>
  );
}

type DumpListEntry = {
  id: number;
  title: string;
  description: string;
  icon: JSX.Element;
  leftLabel?: string;
  rightLabel?: string;
  danger?: boolean;
};

function DumpList(props: {
  label: string;
  entries: DumpListEntry[];
  loading?: boolean;
}) {
  return (
    <div class="h-full w-full">
      <div class="mb-4 flex h-11 items-center justify-between bg-[rgba(255,255,255,.08)] pl-4 pr-2 leading-none">
        <p class="flex">
          {props.label}
          <Show when={props.loading}>
            <span class="ml-2 lds-dual-ring" />
          </Show>
        </p>
        <div class="flex items-center gap-1" />
      </div>
      <div class="h-[calc(100%-4.75rem)] w-full overflow-x-hidden overflow-y-auto pr-1">
        <ul class="list flex flex-col gap-2">
          <For each={props.entries}>
            {(entry) => (
              <li class="item-list-bg relative flex h-auto min-h-[5.5rem] cursor-pointer gap-4 bg-gray-gradient p-3">
                <div>
                  <div class="flex h-[3.75rem] w-[3.75rem] items-center justify-center bg-[rgba(255,255,255,.06)]">
                    {entry.icon}
                  </div>
                </div>
                <div class="w-[66%]">
                  <div>
                    <p style={{ color: entry.danger ? "#c84d4d" : "#fff" }}>
                      {entry.title}
                    </p>
                  </div>
                  <p class="break-words pr-6 leading-none text-white-55">
                    {plainText(entry.description).slice(0, 100)}
                  </p>
                  <ul class="flex pt-2">
                    <Show when={entry.leftLabel}>
                      <li class="flex h-5 bg-cyan/75 px-1 text-xs font-medium leading-5">
                        <p class="text-gray-900">{entry.leftLabel}</p>
                      </li>
                    </Show>
                    <Show when={entry.rightLabel}>
                      <li class="flex h-5 bg-[rgba(255,255,255,.11)] px-2 text-xs leading-5">
                        <p>{entry.rightLabel}</p>
                      </li>
                    </Show>
                  </ul>
                </div>
              </li>
            )}
          </For>
        </ul>
      </div>
    </div>
  );
}

function emptyPage<T>(): PageResult<T> {
  return {
    items: [],
    meta: { page: 1, limit: 50, total: 0, pages: 0 },
  };
}

function plainText(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function expiresIn(value: string) {
  const difference = new Date(value).getTime() - Date.now();
  if (difference < 0) return "Expired";
  const minutes = Math.floor(difference / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (hours < 1) return `Expires in ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  if (hours < 24) return `Expires in ${hours} ${hours === 1 ? "hour" : "hours"}`;
  return `Expires in ${days} ${days === 1 ? "day" : "days"}`;
}
